import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';
import {
  ContestMatchScoreReportRequest,
  MatchScoreReportResponse,
  UpsertMatchScoreReportRequest
} from './match-score-reports-api.types';

@Injectable({ providedIn: 'root' })
export class MatchScoreReportsApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  getReport(tournamentSlug: string, matchId: number): Observable<MatchScoreReportResponse> {
    return this.http.get<MatchScoreReportResponse>(
      `${this.apiBaseUrl}/api/tournaments/${tournamentSlug}/matches/${matchId}/score-report`,
      { headers: this.authHeaders() }
    );
  }

  upsertReport(
    tournamentSlug: string,
    matchId: number,
    payload: UpsertMatchScoreReportRequest
  ): Observable<MatchScoreReportResponse> {
    const url = `${this.apiBaseUrl}/api/tournaments/${tournamentSlug}/matches/${matchId}/score-report`;
    const hasUpload = payload.screenshot_file instanceof File;

    if (!hasUpload) {
      const { screenshot_file, ...jsonPayload } = payload;
      return this.http.put<MatchScoreReportResponse>(
        url,
        { score_report: jsonPayload },
        { headers: this.authHeaders() }
      );
    }

    const formData = new FormData();
    formData.append('score_report[reporting_team_id]', String(payload.reporting_team_id));
    formData.append('score_report[score_team1]', String(payload.score_team1));
    formData.append('score_report[score_team2]', String(payload.score_team2));
    formData.append('score_report[note]', payload.note ?? '');
    formData.append('score_report[keep_existing_screenshot]', String(payload.keep_existing_screenshot ?? false));
    formData.append('score_report[remove_existing_screenshot]', String(payload.remove_existing_screenshot ?? false));
    formData.append('score_report[screenshot_file]', payload.screenshot_file as File);

    (payload.games ?? []).forEach((game, index) => {
      formData.append(`score_report[games][${index}][game_number]`, String(game.game_number));
      formData.append(`score_report[games][${index}][map_name]`, game.map_name ?? '');
      formData.append(`score_report[games][${index}][team1_score]`, String(game.team1_score));
      formData.append(`score_report[games][${index}][team2_score]`, String(game.team2_score));
    });

    return this.http.put<MatchScoreReportResponse>(
      url,
      formData,
      { headers: this.authHeaders() }
    );
  }

  contestReport(
    tournamentSlug: string,
    matchId: number,
    payload: ContestMatchScoreReportRequest
  ): Observable<MatchScoreReportResponse> {
    return this.http.patch<MatchScoreReportResponse>(
      `${this.apiBaseUrl}/api/tournaments/${tournamentSlug}/matches/${matchId}/score-report/contest`,
      { contestation: payload },
      { headers: this.authHeaders() }
    );
  }

  private authHeaders(): HttpHeaders {
    const token = this.session.token();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
